import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { BundledPackagePrice } from "@b2bportal/core-packages";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePackagePrice } from "@tenant/packages";
import { useGetPriceDisplay } from "@tenant/ui";
import { PackageBaseSelectors } from "@/store";

// Savings only come from hotel discounts, so packages without a hotel have none and the
// summary hides for them. Below this the savings look silly (RBC-1400, agreed with
// product). RBC is CAD only, so a plain number is enough.
const MIN_PACKAGE_SAVINGS_TO_DISPLAY = 10;

export const usePackagePriceSummary = (priceOverride?: BundledPackagePrice) => {
  const { t } = useI18nContext();
  const { getPriceDisplay } = useGetPriceDisplay();

  const currentActivePackage = useSelector(
    PackageBaseSelectors.getCurrentActivePackage
  );

  const price = priceOverride ?? currentActivePackage?.price;

  const {
    context: { showPointsPricing, hasTenantSpecific, text, packageType },
  } = usePackagePrice(price);

  const totalPackageSavings = useMemo(() => {
    if (!price?.packageSavings) return null;

    const { priceText } = getPriceDisplay({
      prices: price.packageSavings,
    });

    return t("core-packages.priceSummary.packageSavings", {
      price: priceText,
    });
  }, [price?.packageSavings, getPriceDisplay, t]);

  const shouldDisplay =
    price?.totalPrice != null &&
    (price.packageSavings?.fiat.value ?? 0) >= MIN_PACKAGE_SAVINGS_TO_DISPLAY;

  return {
    context: {
      shouldDisplay,
      title: t("core-packages.priceSummary.title"),
      totalPackageSavings,
      showPointsPricing,
      hasTenantSpecific,
      text,
      packageType,
    },
  };
};
